import { Star, Instagram, Store, QrCode } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCountUp } from "@/hooks/useCountUp";

interface StatCardProps {
  icon: React.ElementType;
  endValue: number;
  prefix?: string;
  suffix?: string;
  label: string;
  gradient: string;
  glowColor: string;
  index: number;
}

const StatCard = ({ icon: Icon, endValue, prefix = "", suffix = "", label, gradient, glowColor, index }: StatCardProps) => {
  const { t } = useLanguage();
  const { ref, formattedValue } = useCountUp({ 
    end: endValue, 
    duration: 2000, 
    prefix, 
    suffix,
    decimals: 0 
  });

  return (
    <div 
      ref={ref}
      className="group relative bg-card rounded-xl p-4 transition-all duration-500 hover:-translate-y-1 hover:shadow-xl border border-border overflow-hidden"
      style={{ 
        animationDelay: `${index * 100}ms`,
      }}
    >
      {/* Background gradient on hover */}
      <div 
        className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-[0.03] transition-opacity duration-500`}
      />
      
      {/* Glow effect on hover */}
      <div 
        className="absolute -inset-1 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-lg pointer-events-none"
        style={{ background: `linear-gradient(135deg, ${glowColor}10, ${glowColor}05)` }}
      />
      
      {/* Top accent line */}
      <div 
        className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${gradient} transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500`}
      />
      
      <div className="relative z-10">
        {/* Icon */}
        <div className="mb-3">
          <div 
            className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} bg-opacity-20 backdrop-blur-sm border border-white/50 flex items-center justify-center transition-all duration-500 group-hover:scale-105`}
            style={{ 
              background: `linear-gradient(135deg, ${glowColor}20, ${glowColor}10)`,
              boxShadow: `0 2px 8px -2px ${glowColor}20`
            }}
          >
            <Icon className={`h-5 w-5`} style={{ color: glowColor }} />
          </div>
        </div>
        
        {/* Value */}
        <div className={`text-2xl lg:text-3xl font-display font-bold bg-gradient-to-r ${gradient} bg-clip-text text-transparent tabular-nums mb-1`}>
          {formattedValue}
        </div>
        
        {/* Label */}
        <p className="text-xs text-slate-500 font-medium group-hover:text-slate-600 transition-colors duration-300">
          {label}
        </p>
      </div>
    </div>
  );
};

const Stats = () => {
  const { language } = useLanguage();
  
  const stats = [
    { 
      icon: Star, 
      endValue: 15000, 
      prefix: "+", 
      suffix: "", 
      label: language === "fr" ? "Avis collectés" : "Reviews collected", 
      gradient: "from-rose-400 to-violet-500", 
      glowColor: "#f43f5e" 
    },
    { 
      icon: Instagram, 
      endValue: 8500, 
      prefix: "+", 
      suffix: "", 
      label: language === "fr" ? "Nouveaux abonnés" : "New followers", 
      gradient: "from-pink-400 to-violet-500", 
      glowColor: "#ec4899" 
    },
    { 
      icon: Store, 
      endValue: 250, 
      prefix: "", 
      suffix: "+", 
      label: language === "fr" ? "Commerces actifs" : "Active businesses", 
      gradient: "from-amber-400 to-orange-600", 
      glowColor: "#f59e0b" 
    },
    { 
      icon: QrCode, 
      endValue: 45000, 
      prefix: "", 
      suffix: "", 
      label: language === "fr" ? "QR codes scannés" : "QR codes scanned", 
      gradient: "from-emerald-400 to-teal-600", 
      glowColor: "#10b981" 
    },
  ];

  return (
    <section aria-label="Statistiques clés Localove" className="py-10 lg:py-12 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="sr-only">{language === "fr" ? "Nos chiffres clés" : "Our key metrics"}</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
          {stats.map((stat, index) => (
            <StatCard
              key={index}
              index={index}
              icon={stat.icon}
              endValue={stat.endValue}
              prefix={stat.prefix}
              suffix={stat.suffix}
              label={stat.label}
              gradient={stat.gradient}
              glowColor={stat.glowColor}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stats;